import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { DbUser } from "../../models";
import { BaseDbObject } from "../../models/db";
import { Customer } from "../reports";
import {
  fetchApiDelete,
  fetchApiGet,
  fetchApiPost,
  fetchApiPut,
} from "../utils";

// Enums
export enum TaskPriority {
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH",
  CRITICAL = "CRITICAL",
}

export enum RecurrencePattern {
  DAILY = "DAILY",
  WEEKLY = "WEEKLY",
  MONTHLY = "MONTHLY",
  QUARTERLY = "QUARTERLY",
  BIANNUALLY = "BIANNUALLY",
  ANNUALLY = "ANNUALLY",
}

export enum RecurrenceCalculationType {
  FIXED_SCHEDULE = "FIXED_SCHEDULE",
  FROM_COMPLETION = "FROM_COMPLETION",
}

export enum InstanceStatus {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

export enum SecondaryInstanceStatus {
  NonAppl = "NON_APPLICABLE",
}

// Interfaces
export interface User {
  id: string;
  name: string;
  email: string;
}

export interface Task extends BaseDbObject {
  title: string;
  description?: string;
  note?: string;
  priority: TaskPriority;
  recurrence_pattern?: RecurrencePattern;
  recurrence_interval?: number;
  recurrence_calculation_type?: RecurrenceCalculationType;
  is_active: boolean;
  created_by_id: string;
  created_by?: User;
  instances: TaskInstance[]
  template_id?: string;
}

export interface TaskInstance extends BaseDbObject {
  task_id: string;
  task?: Task;
  assigned_to_id: string;
  assigned_to?: User;
  due_date: string;
  completed_at?: string;
  is_late: boolean;
  status: InstanceStatus;
  secondary_status?: SecondaryInstanceStatus;
  created_by_id: string;
  template_instance_id?: string;

  template_instance?: { trigger_meta_data: any; template?: { name: string } };
}

export interface TaskHistory {
  id: string;
  task_id: string;
  changed_by_id: string;
  changed_by?: User;
  change_type: string;
  old_value?: any;
  new_value?: any;
  created_at: string;
}

export interface InstanceHistory {
  id: string;
  instance_id: string;
  changed_by_id: string;
  changed_by?: User;
  change_type: string;
  old_value?: any;
  new_value?: any;
  created_at: string;
}

// Request/Response Types
export const CreateTaskRequest = z.object({
  title: z.string().min(1).max(200),
  description: z.string().optional(),
  note: z.string().optional(),
  priority: z.nativeEnum(TaskPriority),
  recurrence_pattern: z.nativeEnum(RecurrencePattern).optional(),
  recurrence_interval: z.number().min(1).optional(),
  recurrence_calculation_type: z.nativeEnum(RecurrenceCalculationType).optional(),
  is_active: z.boolean().optional(),
});

export type CreateTaskRequest = z.infer<typeof CreateTaskRequest>;

export const UpdateTaskRequest = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().optional(),
  note: z.string().optional(),
  priority: z.nativeEnum(TaskPriority).optional(),
  recurrence_pattern: z.nativeEnum(RecurrencePattern).optional(),
  recurrence_interval: z.number().min(1).optional(),
  recurrence_calculation_type: z.nativeEnum(RecurrenceCalculationType).optional(),
  is_active: z.boolean().optional(),
});

export type UpdateTaskRequest = z.infer<typeof UpdateTaskRequest>;

export interface ListTasksParams {
  page?: number;
  limit?: number;
  is_active?: boolean;
  search?: string;
}

export interface ListTasksResponse {
  tasks: Task[];
  total: number;
  page: number;
  limit: number;
}

export interface TaskDetailResponse extends Task {
  instances: TaskInstance[];
  histories: TaskHistory[];
  instance_histories: InstanceHistory[];
}

export const CreateInstancesRequest = z.object({
  task_id: z.string().uuid(),
  assignee_ids: z.array(z.string().uuid()).min(1),
  due_date: z.string(),
});

export type CreateInstancesRequest = z.infer<typeof CreateInstancesRequest>;

export interface ListInstancesParams {
  page?: number;
  limit?: number;
  status?: InstanceStatus;
  assigned_to_id?: string;
  task_id?: string;
  is_late?: boolean;
  due_date_from?: string;
  due_date_to?: string;
  search?: string;
}

export interface ListInstancesResponse {
  data: TaskInstance[];
  total: number;
  page: number;
  limit: number;
}

export const CompleteInstanceRequest = z.object({
  completed_at: z.string().optional(),
});

export type CompleteInstanceRequest = z.infer<typeof CompleteInstanceRequest>;

export const ReassignInstanceRequest = z.object({
  assigned_to_id: z.string().uuid(),
});

export type ReassignInstanceRequest = z.infer<typeof ReassignInstanceRequest>;

export interface UpcomingInstancesParams {
  days_ahead?: number;
  assigned_to_id?: string;
}

// API Hooks - Task Management
export function useListTasks(params?: ListTasksParams) {
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.page) queryParams.set("page", params.page.toString());
    if (params.limit) queryParams.set("limit", params.limit.toString());
    if (params.is_active !== undefined) queryParams.set("is_active", params.is_active.toString());
    if (params.search) queryParams.set("search", params.search);
  }

  return useQuery({
    queryKey: ["tasks", "list", queryParams.toString()],
    queryFn: () => fetchApiGet<ListTasksResponse>("tasks", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useGetTask(taskId: string) {
  return useQuery({
    queryKey: ["tasks", "detail", taskId],
    queryFn: () => fetchApiGet<TaskDetailResponse>(`tasks/${taskId}`),
    retry: 2,
    enabled: !!taskId,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTaskRequest) =>
      fetchApiPost<Task>("tasks", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useUpdateTask(taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTaskRequest) =>
      fetchApiPut<Task>(`tasks/${taskId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) =>
      fetchApiDelete(`tasks/${taskId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

// API Hooks - Task Instance Management
export function useListInstances(params?: ListInstancesParams) {
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.page) queryParams.set("page", params.page.toString());
    if (params.limit) queryParams.set("limit", params.limit.toString());
    if (params.status) queryParams.set("status", params.status);
    if (params.search) queryParams.set("search", params.search);
    if (params.assigned_to_id) queryParams.set("assigned_to_id", params.assigned_to_id);
    if (params.task_id) queryParams.set("task_id", params.task_id);
    if (params.is_late !== undefined) queryParams.set("is_late", params.is_late.toString());
    if (params.due_date_from) queryParams.set("due_date_from", params.due_date_from);
    if (params.due_date_to) queryParams.set("due_date_to", params.due_date_to);
  }

  return useQuery({
    queryKey: ["task-instances", "list", queryParams.toString()],
    queryFn: () => fetchApiGet<ListInstancesResponse>("tasks/instances", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useCreateInstances() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInstancesRequest) =>
      fetchApiPost<TaskInstance[]>("tasks/instances", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-instances"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useOverdueInstances() {
  return useQuery({
    queryKey: ["task-instances", "overdue"],
    queryFn: () => fetchApiGet<TaskInstance[]>("tasks/instances/overdue"),
    retry: 2,
  });
}

export function useUpcomingInstances(params?: UpcomingInstancesParams) {
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.days_ahead) queryParams.set("days_ahead", params.days_ahead.toString());
    if (params.assigned_to_id) queryParams.set("assigned_to_id", params.assigned_to_id);
  }

  return useQuery({
    queryKey: ["task-instances", "upcoming", queryParams.toString()],
    queryFn: () => fetchApiGet<TaskInstance[]>("tasks/instances/upcoming", queryParams),
    retry: 2,
  });
}

export function useCompleteInstance(instanceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data?: CompleteInstanceRequest) =>
      fetchApiPost<TaskInstance>(`tasks/instances/${instanceId}/complete`, data || {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-instances"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useMarkAsNonApplicableInstance(instanceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data?: CompleteInstanceRequest) =>
      fetchApiPost<TaskInstance>(`tasks/instances/${instanceId}/non_applicable`, data || {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-instances"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useReassignInstance(instanceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReassignInstanceRequest) =>
      fetchApiPut<TaskInstance>(`tasks/instances/${instanceId}/reassign`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-instances"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}
